import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getRiders, getRider, createRider, updateRider, deleteRider } from '../api/riders';

const roundTo8Decimals = (value: number): number => {
  return Math.round(value * 1e8) / 1e8;
};

const convertRider = (rider: any): any => {
  // Round to 5 decimal places, for float precision errors like 0.71 * 60.0 / 60.0 = 0.7099999999999999
  rider.time_ref_minutes = roundTo8Decimals(rider.time_ref / 60.0);
  rider.distance_ref_km = roundTo8Decimals(rider.distance_ref / 1000.0);
  rider.speed_ref_kmh = roundTo8Decimals(rider.speed_ref * 3.6);
  return rider;
};

const convertRiderBack = (rider: any): any => {
  rider.time_ref = rider.time_ref_minutes * 60.0;
  rider.distance_ref = rider.distance_ref_km * 1000.0;
  rider.speed_ref = rider.speed_ref_kmh / 3.6;
  return rider;
};

const convertRiders = (riders: any): any => {
  return riders.map(convertRider);
};

export const useRiders = () =>
  useQuery({ queryKey: ['riders'], queryFn: async () => convertRiders(await getRiders()) });

export const useRider = (riderId: string) =>
  useQuery({
    queryKey: ['rider', riderId],
    queryFn: async () => convertRider(await getRider(riderId)),
    enabled: !!riderId && riderId !== 'new',
  });

export const useRidersById = () =>
  useQuery({
    queryKey: ['ridersById'],
    queryFn: async () => {
      const riders = convertRiders(await getRiders());
      return new Map<string, any>(riders?.map((r: any) => [r.id, r]));
    },
  });

export const useCreateRider = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (rider: any) => createRider(convertRiderBack(rider)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['riders'] });
      queryClient.invalidateQueries({ queryKey: ['ridersById'] });
    },
  });
};

export const useUpdateRider = (riderId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (rider: any) => updateRider(riderId, convertRiderBack(rider)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['riders'] });
      queryClient.invalidateQueries({ queryKey: ['ridersById'] });
    },
  });
};

export const useDeleteRider = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (riderId: string) => deleteRider(riderId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['riders'] });
      queryClient.invalidateQueries({ queryKey: ['ridersById'] });
    },
  });
};
